/**
 * CityAutocomplete Component
 * Unified autocomplete for city selection
 * Combines Google Places API with local ZIP database for comprehensive coverage
 * (including small towns like West Creek, NJ that Google doesn't have)
 *
 * Single-click selection: clicking any result immediately selects it
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { searchCitiesByName } from '../../lib/utils/geo'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
const ALLOWED_COUNTRIES = ['US', 'FR']
const GOOGLE_COUNTRY_RESTRICTIONS = ALLOWED_COUNTRIES.map(country => country.toLowerCase())

function isAllowedCity(cityData) {
  return !cityData.country || ALLOWED_COUNTRIES.includes(cityData.country)
}

function unsupportedLocationMessage(cityData) {
  return `We currently only serve events in the United States and France. ${cityData.countryName || cityData.country ? `(${cityData.countryName || cityData.country} is not yet available)` : ''}`
}

export default function CityAutocomplete({
  value,
  onChange,
  onCitySelect,
  placeholder = "Start typing city name...",
  className = "",
  required = false,
  id = "city-autocomplete",
  name = "city"
}) {
  const inputRef = useRef(null)
  const dropdownRef = useRef(null)
  const autocompleteServiceRef = useRef(null)
  const placesServiceRef = useRef(null)
  const sessionTokenRef = useRef(null)

  const [mapsLoaded, setMapsLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [inputValue, setInputValue] = useState(value || '')
  const [showDropdown, setShowDropdown] = useState(false)
  const [googleResults, setGoogleResults] = useState([])
  const [localResults, setLocalResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // Load Google Maps script
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn('Google Maps API key not found')
      setLoadError(true)
      return
    }

    if (window.google?.maps?.places) {
      setMapsLoaded(true)
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => setMapsLoaded(true)
    script.onerror = () => setLoadError(true)

    document.head.appendChild(script)
  }, [])

  // Initialize Google services (without UI - we render our own dropdown)
  useEffect(() => {
    if (!mapsLoaded) return

    try {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService()
      // Create a dummy div for PlacesService (required by API)
      const dummyDiv = document.createElement('div')
      placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv)
      // Create session token for billing optimization
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken()
    } catch (error) {
      console.error('Error initializing Google Places services:', error)
    }
  }, [mapsLoaded])

  // Search both Google and local database
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setGoogleResults([])
      setLocalResults([])
      setShowDropdown(false)
      return
    }

    setIsLoading(true)

    // Search local database (synchronous, fast)
    const localMatches = searchCitiesByName(query, 5)
    setLocalResults(localMatches)

    // Search Google Places (asynchronous)
    if (autocompleteServiceRef.current) {
      try {
        const request = {
          input: query,
          types: ['(regions)'],
          componentRestrictions: { country: GOOGLE_COUNTRY_RESTRICTIONS },
          sessionToken: sessionTokenRef.current
        }

        autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            // Filter to show mainly cities/localities
            const cityPredictions = predictions
              .filter(p => {
                const types = p.types || []
                return types.includes('locality') ||
                       types.includes('sublocality') ||
                       types.includes('neighborhood') ||
                       types.includes('postal_code')
              })
              .slice(0, 5)
              .map(p => ({
                placeId: p.place_id,
                description: p.description,
                mainText: p.structured_formatting?.main_text || p.description,
                secondaryText: p.structured_formatting?.secondary_text || '',
                isGoogle: true
              }))
            setGoogleResults(cityPredictions)
          } else {
            setGoogleResults([])
          }
          setIsLoading(false)
        })
      } catch (error) {
        console.error('Error fetching Google predictions:', error)
        setGoogleResults([])
        setIsLoading(false)
      }
    } else {
      setIsLoading(false)
    }

    setShowDropdown(true)
  }, [])

  // Handle input change with debounce
  const debounceRef = useRef(null)
  const handleInputChange = useCallback((e) => {
    const query = e.target.value
    setInputValue(query)

    if (onChange) {
      onChange(e)
    }

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      performSearch(query)
    }, 200)
  }, [onChange, performSearch])

  // Handle Google result selection (single click)
  const handleGoogleSelect = useCallback(async (result) => {
    console.log('🌐 Google place selected:', result)

    if (!placesServiceRef.current) return

    // Get place details
    const request = {
      placeId: result.placeId,
      fields: ['address_components', 'geometry', 'name'],
      sessionToken: sessionTokenRef.current
    }

    placesServiceRef.current.getDetails(request, async (place, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
        console.error('Failed to get place details')
        return
      }

      // Reset session token after selection
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken()

      const cityData = {
        city: '',
        state: '',
        zip_code: '',
        latitude: null,
        longitude: null,
        country: ''
      }

      // Parse address components
      place.address_components?.forEach(component => {
        const types = component.types
        if (types.includes('country')) {
          cityData.country = component.short_name  // e.g. 'US', 'FR'
          cityData.countryName = component.long_name  // e.g. 'United States', 'France'
        }
        if (types.includes('administrative_area_level_1')) {
          cityData.state = component.short_name
        }
        if (types.includes('locality')) {
          cityData.city = component.long_name
        }
        if (types.includes('postal_code')) {
          cityData.zip_code = component.long_name
        }
      })

      // For international cities, use country name as "state" so display reads "Paris, France"
      if (cityData.country && cityData.country !== 'US') {
        cityData.state = cityData.countryName || cityData.country
      }

      // Enforce US and France only.
      if (cityData.country && (!ALLOWED_COUNTRIES.includes(cityData.country) || !isAllowedCity(cityData))) {
        setShowDropdown(false)
        setGoogleResults([])
        setLocalResults([])
        setInputValue('')
        if (inputRef.current) inputRef.current.value = ''
        // Notify parent with error so the form can display a message
        if (onCitySelect) {
          onCitySelect({
            error: 'unsupported_country',
            country: cityData.countryName || cityData.country,
            message: unsupportedLocationMessage(cityData)
          })
        }
        return
      }

      // Fallbacks for city name
      if (!cityData.city) {
        place.address_components?.forEach(component => {
          const types = component.types
          if ((types.includes('sublocality_level_1') || types.includes('neighborhood')) && !cityData.city) {
            cityData.city = component.long_name
          }
        })
      }

      // Get coordinates
      if (place.geometry?.location) {
        cityData.latitude = place.geometry.location.lat()
        cityData.longitude = place.geometry.location.lng()
      }

      // If no ZIP, try reverse geocoding
      if (!cityData.zip_code && cityData.latitude && cityData.longitude) {
        try {
          const geocoder = new window.google.maps.Geocoder()
          const geoResult = await new Promise((resolve, reject) => {
            geocoder.geocode(
              { location: { lat: cityData.latitude, lng: cityData.longitude } },
              (results, geoStatus) => {
                if (geoStatus === 'OK' && results[0]) resolve(results[0])
                else reject(new Error('Geocoding failed'))
              }
            )
          })
          geoResult.address_components?.forEach(component => {
            if (component.types.includes('postal_code')) {
              cityData.zip_code = component.long_name
            }
          })
        } catch (error) {
          console.warn('Could not fetch ZIP code:', error)
        }
      }

      console.log('🏙️ City data extracted:', cityData)

      // Update input and close dropdown immediately
      if (cityData.city && cityData.state) {
        const displayValue = `${cityData.city}, ${cityData.state}`
        setInputValue(displayValue)
        if (inputRef.current) inputRef.current.value = displayValue
      }

      setShowDropdown(false)
      setGoogleResults([])
      setLocalResults([])

      // Notify parent component
      if (onCitySelect && cityData.city) {
        onCitySelect(cityData)
      }
    })
  }, [onCitySelect])

  // Handle local result selection (single click)
  const handleLocalSelect = useCallback((result) => {
    console.log('📍 Local city selected:', result)

    // Update input and close dropdown immediately
    const displayValue = `${result.city}, ${result.state}`
    setInputValue(displayValue)
    if (inputRef.current) inputRef.current.value = displayValue

    setShowDropdown(false)
    setGoogleResults([])
    setLocalResults([])

    // Notify parent component
    if (onCitySelect) {
      onCitySelect({
        city: result.city,
        state: result.state,
        zip_code: result.zip,
        latitude: result.latitude,
        longitude: result.longitude,
        country: 'US',
        countryName: 'United States'
      })
    }
  }, [onCitySelect])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle focus to show dropdown if there are results
  const handleFocus = useCallback(() => {
    if (googleResults.length > 0 || localResults.length > 0) {
      setShowDropdown(true)
    }
  }, [googleResults.length, localResults.length])

  const hasResults = googleResults.length > 0 || localResults.length > 0

  // Fallback if Maps fails - just show local results
  if (loadError) {
    return (
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={className}
          required={required}
          id={id}
          autoComplete="off"
        />
        {showDropdown && localResults.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-[1001] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {localResults.map((result, index) => (
              <button
                key={`local-${result.city}-${result.state}-${index}`}
                type="button"
                onClick={() => handleLocalSelect(result)}
                className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
              >
                <span className="font-medium">{result.city}</span>
                <span className="text-gray-500">, {result.state}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        name={name}
        defaultValue={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        required={required}
        id={id}
        autoComplete="off"
      />

      {/* Unified dropdown - single click on any item selects it */}
      {showDropdown && hasResults && (
        <div
          ref={dropdownRef}
          className="absolute z-[1001] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-y-auto"
        >
          {/* Google Places results */}
          {googleResults.length > 0 && (
            <>
              {googleResults.map((result, index) => (
                <button
                  key={`google-${result.placeId}-${index}`}
                  type="button"
                  onClick={() => handleGoogleSelect(result)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100"
                >
                  <span className="font-medium">{result.mainText}</span>
                  {result.secondaryText && (
                    <span className="text-gray-500 text-sm ml-1">{result.secondaryText}</span>
                  )}
                </button>
              ))}
            </>
          )}

          {/* Local database results (small towns) */}
          {localResults.length > 0 && (
            <>
              {googleResults.length > 0 && (
                <div className="px-3 py-1.5 text-xs text-gray-500 bg-gray-50 border-b border-t">
                  More locations
                </div>
              )}
              {localResults.map((result, index) => (
                <button
                  key={`local-${result.city}-${result.state}-${index}`}
                  type="button"
                  onClick={() => handleLocalSelect(result)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                >
                  <span className="font-medium">{result.city}</span>
                  <span className="text-gray-500">, {result.state}</span>
                </button>
              ))}
            </>
          )}

          {isLoading && googleResults.length === 0 && localResults.length === 0 && (
            <div className="px-3 py-2 text-gray-500 text-sm">Searching...</div>
          )}
        </div>
      )}
    </div>
  )
}
